<script setup lang="ts">
import { useTranslation } from "i18next-vue";
import type { FieldObservationType } from "@/types/field-observations.types";
import type { ObservationsViewMode } from "@/stores/ObservationsPanelState";
import { getObservationTypeConfig } from "@/components/field-observations/observation-type";
import PhosphorIcon from "@/components/UI/PhosphorIcon.vue";

/**
 * Barre d'outils de la liste d'observations, collée en haut de la zone de
 * défilement : filtre par type (plusieurs types cumulables, vide = tous),
 * accès à la popup des marqueurs photo du plan, et mode de tri — par date,
 * par partie (sections au chemin complet) ou en arborescence.
 */

defineProps<{
  /** Types affichés — vide = tous. */
  typeFilter: Set<FieldObservationType>;
  viewMode: ObservationsViewMode;
  /** La popup des marqueurs photo est ouverte. */
  markerPickerOpen: boolean;
}>();

defineEmits<{
  (e: "toggle-type", type: FieldObservationType): void;
  (e: "update:viewMode", mode: ObservationsViewMode): void;
  (e: "toggle-marker-picker"): void;
}>();

const { t } = useTranslation();

const TYPES: FieldObservationType[] = ["DISORDER", "OVERVIEW", "COMMENT"];
</script>

<template>
  <div class="list-toolbar d-flex align-items-center gap-1">
    <el-dropdown trigger="click" :hide-on-click="false">
      <button
        type="button"
        class="filter-btn d-inline-flex align-items-center gap-1"
        :class="{ active: typeFilter.size > 0 }"
      >
        <PhosphorIcon name="funnel-simple" :size="14" weight="bold" />
        {{ t("canvas:observations.filters.type") }}
        <span v-if="typeFilter.size" class="filter-count">
          {{ typeFilter.size }}
        </span>
      </button>
      <template #dropdown>
        <el-dropdown-menu>
          <el-dropdown-item
            v-for="type in TYPES"
            :key="type"
            @click="$emit('toggle-type', type)"
          >
            <span class="d-inline-flex align-items-center gap-2 filter-option">
              <PhosphorIcon
                :name="getObservationTypeConfig(type).icon"
                :size="15"
                weight="bold"
                class="option-icon"
              />
              <span class="flex-grow-1">
                {{ t(`dashboard:fieldObservations.type.${type}`) }}
              </span>
              <PhosphorIcon
                v-if="typeFilter.has(type)"
                name="check"
                :size="14"
                weight="bold"
              />
            </span>
          </el-dropdown-item>
        </el-dropdown-menu>
      </template>
    </el-dropdown>

    <button
      type="button"
      class="filter-btn d-inline-flex align-items-center gap-1"
      :class="{ active: markerPickerOpen }"
      :title="t('canvas:observations.markerPicker.title')"
      @click="$emit('toggle-marker-picker')"
    >
      <PhosphorIcon name="camera" :size="14" weight="bold" />
    </button>

    <div class="flex-grow-1" />

    <div class="sort-group d-inline-flex">
      <button
        type="button"
        class="sort-btn"
        :class="{ active: viewMode === 'date' }"
        :title="t('canvas:observations.sort.date')"
        @click="$emit('update:viewMode', 'date')"
      >
        <PhosphorIcon name="clock" :size="14" weight="bold" />
      </button>
      <button
        type="button"
        class="sort-btn"
        :class="{ active: viewMode === 'part' }"
        :title="t('canvas:observations.sort.part')"
        @click="$emit('update:viewMode', 'part')"
      >
        <PhosphorIcon name="list-dashes" :size="14" weight="bold" />
      </button>
      <button
        type="button"
        class="sort-btn"
        :class="{ active: viewMode === 'tree' }"
        :title="t('canvas:observations.sort.tree')"
        @click="$emit('update:viewMode', 'tree')"
      >
        <PhosphorIcon name="tree-structure" :size="14" weight="bold" />
      </button>
    </div>

    <!-- Outil en bout de barre (outillage de développement) -->
    <slot name="extra" />
  </div>
</template>

<style lang="scss" scoped>
.list-toolbar {
  border-top: 1px solid rgba(255, 255, 255, 0.12);
  border-bottom: 1px solid rgba(255, 255, 255, 0.12);
  // Toujours visible : collée en haut de la zone de scroll du volet, sur fond
  // opaque. PAS de marge négative pour déborder du padding : `.scroll-y` a un
  // `overflow-y: auto` (donc `overflow-x` calculé à `auto`), une largeur
  // excédentaire y crée une barre horizontale qui rogne la hauteur utile — et
  // fait apparaître un micro-scroll vertical.
  position: sticky;
  top: 0;
  z-index: 2;
  margin-bottom: 8px;
  padding: 6px 0;
  background-color: var(--canvas-bg);
}

.filter-btn {
  border: none;
  border-radius: var(--bs-border-radius);
  background-color: rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.75rem;
  padding: 4px 9px;

  &:hover {
    background-color: rgba(255, 255, 255, 0.12);
  }

  &.active {
    color: white;
  }

  .filter-count {
    background-color: var(--bs-primary);
    color: white;
    border-radius: 8px;
    padding: 0 5px;
    font-size: 0.68rem;
  }
}

.filter-option {
  min-width: 160px;

  // Même icône que sur les cartes, dans le bleu des observations (éclairci :
  // le menu est sombre dans l'outil dessin).
  .option-icon {
    color: color-mix(in srgb, var(--bs-primary) 65%, white);
    flex-shrink: 0;
  }
}

.sort-group {
  border-radius: var(--bs-border-radius);
  overflow: hidden;
  background-color: rgba(255, 255, 255, 0.06);
}

.sort-btn {
  border: none;
  background: transparent;
  color: rgba(255, 255, 255, 0.6);
  padding: 4px 8px;

  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }

  &.active {
    background-color: var(--bs-primary);
    color: white;
  }
}
</style>
